import React, { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaGamepad, FaTag } from "react-icons/fa";
import { Button, Input } from "../../ui";
import { resolveCode } from "../../utils/resolveCode";
import { pricingPath, savePendingDiscount } from "../../utils/pendingDiscount";

// One box for any code a visitor has: a game code opens the game, a website
// discount code goes to the pricing page with the code applied. People were
// typing discount codes into the old game-code box and being told "wrong".
const CodeEntry = ({
  gamePath = (code) => `/${code}`,
  onDone,
  autoFocus = false,
  size = "md",
  showHelp = true,
  submitLabel = "متابعة",
}) => {
  const navigate = useNavigate();
  // The home page and the header modal can both show this form.
  const uid = useId();
  const inputId = `code-entry-${uid}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    const result = await resolveCode(value);
    setBusy(false);

    if (result.kind === "empty") {
      setError("اكتب الكود أولاً");
      return;
    }
    if (result.kind === "game") {
      onDone?.();
      navigate(gamePath(result.code));
      return;
    }
    if (result.kind === "discount") {
      savePendingDiscount(result.code);
      toast.success(
        result.verified
          ? "هذا كود خصم، طبّقناه لك على الباقات"
          : "هذا كود خصم، سنطبّقه لك في صفحة الاشتراك"
      );
      onDone?.();
      navigate(pricingPath(result.code));
      return;
    }
    setError(result.message);
  };

  const big = size === "lg";

  return (
    <form onSubmit={submit} noValidate>
      <label htmlFor={inputId} className="sr-only">
        الكود
      </label>
      <div className={`flex gap-2 ${big ? "flex-col sm:flex-row" : ""}`}>
        <Input
          id={inputId}
          ltr
          size={big ? "lg" : "md"}
          value={value}
          onChange={(e) => {
            setValue(e.target.value.toUpperCase());
            if (error) setError("");
          }}
          placeholder="ABC123"
          maxLength={40}
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          invalid={!!error}
          aria-describedby={error ? errorId : showHelp ? helpId : undefined}
          className="flex-1 min-w-0 text-center font-bold tracking-[0.2em] placeholder:tracking-[0.2em]"
        />
        <Button
          type="submit"
          size={big ? "lg" : "md"}
          loading={busy}
          iconEnd={<FaArrowLeft className="text-sm" />}
          className={big ? "sm:min-w-[9rem]" : ""}
        >
          {submitLabel}
        </Button>
      </div>

      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-2 text-sm font-medium text-danger"
        >
          {error}
        </p>
      )}

      {showHelp && (
        <ul id={helpId} className="mt-4 space-y-2 text-sm text-white/70">
          <li className="flex items-start gap-2">
            <FaGamepad className="mt-1 shrink-0 text-primary" />
            <span>
              <b className="text-white">كود اللعبة</b> (6 خانات): يفتح لعبتك
              على الشاشة.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <FaTag className="mt-1 shrink-0 text-primary" />
            <span>
              <b className="text-white">كود الخصم</b>: ننقلك لصفحة الاشتراك
              ونطبّقه على الباقات.
            </span>
          </li>
        </ul>
      )}
    </form>
  );
};

export default CodeEntry;
